/**
 * routeHelpers.jsx — Helpers React Router pour la plateforme.
 *
 * Exportés ici pour pouvoir être réutilisés depuis tous les modules de
 * routes (`/app/frontend/src/routes/`) sans réimporter Layout/Guards
 * partout.
 *
 * Trois helpers :
 *  - withLayout(Component)        : enrobe le composant dans `<Layout>`
 *  - withStudentLayout(Component) : Layout + garde `StudentOnlyRoute`
 *  - TeacherGated(Component, opts): garde "enseignant" + bypass routes pro
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { TeacherAccessGate } from '../components/TeacherAccessGate';
import StudentOnlyRoute from '../components/StudentOnlyRoute';
import { useApp } from '../contexts/AppContext';

export const withLayout = (Component) => (
  <Layout>
    <Component />
  </Layout>
);

/**
 * Layout standard + garde StudentOnlyRoute.
 * Empêche un enseignant/admin connecté d'atterrir sur les pages élèves
 * (Home, Révision, Examen, Profil…). Il sera redirigé vers /enseignant.
 */
export const withStudentLayout = (Component) => (
  <StudentOnlyRoute>
    <Layout>
      <Component />
    </Layout>
  </StudentOnlyRoute>
);

/**
 * Garde réservée aux ENSEIGNANTS validés.
 *
 * Comportement (juin 2026, après demande utilisateur de protection stricte) :
 *  • Visiteur non authentifié → redirection vers `/login` avec state.from
 *  • Utilisateur connecté (élève simple non premium) → écran `TeacherAccessGate`
 *  • Enseignant validé / admin / premium → accès direct au composant
 *
 * Note : la précédente vitrine B2B publique sur `/enseignant/*` est supprimée
 * sur demande métier (lutte contre l'accès libre au contenu pédagogique pro).
 */
export const TeacherGated = (Component, opts = {}) => {
  const TeacherGatedWrapper = (props) => {
    const { user, loading } = useApp();
    const location = useLocation();

    // Phase 31 fix : attend la fin du chargement /auth/me avant de décider.
    // Sans ça, un utilisateur légitime (admin/enseignant) est redirigé vers
    // /login pendant les 200-800ms de résolution du profil car `user` est
    // encore null à l'initial render.
    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center" data-testid="teacher-gated-loading">
          <div className="text-muted-foreground text-sm">Chargement…</div>
        </div>
      );
    }
    if (!user) {
      return <Navigate to="/login" replace state={{ from: location.pathname }} />;
    }
    if (user.is_premium || user.role === 'enseignant' || user.role === 'admin' || user.is_admin) {
      return <Component {...props} />;
    }
    return <TeacherAccessGate title={opts.title} description={opts.description} testid={opts.testid} />;
  };
  TeacherGatedWrapper.displayName = `TeacherGated(${Component.displayName || Component.name || 'Component'})`;
  return TeacherGatedWrapper;
};
